<script setup lang="ts">
import { Head, Link, router, useForm } from '@inertiajs/vue3';
import { Check, Save, Trash2, UserPlus, Wand2 } from 'lucide-vue-next';
import { computed, ref } from 'vue';
import {
    destroy,
    show as projectShow,
    update,
} from '@/actions/App/Http/Controllers/ProjectController';
import {
    destroy as destroyProjectMember,
    store as storeProjectMember,
    update as updateProjectMember,
} from '@/actions/App/Http/Controllers/ProjectMemberController';
import { update as updateResearchConfig } from '@/actions/App/Http/Controllers/ProjectResearchConfigController';
import { autofill } from '@/actions/App/Http/Controllers/ProjectStatsController';
import AiBanner from '@/components/shared/AiBanner.vue';
import ConfirmDialog from '@/components/shared/ConfirmDialog.vue';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/composables/useToast';
import AppLayout from '@/layouts/AppLayout.vue';
import { pricing } from '@/routes';
import {
    importMethod as importMethodRoute,
    searchEquations as searchEquationsRoute,
} from '@/routes/projects';
import type { Project, ProjectMember as ProjectMemberType } from '@/types';

interface Props {
    project: Project;
    canManageCollaboration: boolean;
}

defineOptions({
    layout: (props: { project: Project }) => [
        AppLayout,
        {
            breadcrumbs: [
                { title: 'Projets', href: '/projects' },
                {
                    title: props.project.name,
                    href: projectShow.url(props.project.id),
                },
                { title: 'Paramètres', href: '#' },
            ],
        },
    ],
});

const props = defineProps<Props>();
const toast = useToast();

const isNewProject = computed(
    () =>
        !props.project.research_config &&
        (props.project.papers_count ?? 0) === 0,
);

const form = useForm({
    name: props.project.name,
    description: props.project.description || '',
    status: props.project.status || 'draft',
});

const researchForm = useForm({
    inclusion_criteria:
        props.project.research_config?.inclusion_criteria?.join('\n') || '',
    exclusion_criteria:
        props.project.research_config?.exclusion_criteria?.join('\n') || '',
    languages: props.project.research_config?.languages?.join(', ') || '',
    databases: props.project.research_config?.databases?.join(', ') || '',
    search_strategy: props.project.research_config?.search_strategy || '',
});

const submitBasic = () => {
    form.patch(update.url(props.project.id));
};

const submitResearch = () => {
    researchForm
        .transform((data) => ({
            inclusion_criteria: data.inclusion_criteria
                .split('\n')
                .map((value) => value.trim())
                .filter(Boolean),
            exclusion_criteria: data.exclusion_criteria
                .split('\n')
                .map((value) => value.trim())
                .filter(Boolean),
            languages: data.languages
                .split(',')
                .map((value) => value.trim())
                .filter(Boolean),
            databases: data.databases
                .split(',')
                .map((value) => value.trim())
                .filter(Boolean),
            search_strategy: data.search_strategy.trim() || null,
        }))
        .patch(updateResearchConfig.url(props.project.id), {
            preserveScroll: true,
            onSuccess: () => {
                toast.success('Configuration de recherche mise a jour.');
            },
            onError: () => {
                toast.error("Impossible d'enregistrer la configuration.");
            },
        });
};

const inviteForm = useForm({ email: '', role: 'viewer' });
const inviteError = ref<string | null>(null);
const updatingMemberId = ref<string | null>(null);
const removingMemberId = ref<string | null>(null);

const isDeleteDialogOpen = ref(false);

const reloadProject = () => {
    router.reload({ only: ['project'] });
};

const csrfToken = () =>
    document
        .querySelector('meta[name="csrf-token"]')
        ?.getAttribute('content') ?? '';

const xsrfToken = () =>
    decodeURIComponent(
        document.cookie
            .split('; ')
            .find((cookie) => cookie.startsWith('XSRF-TOKEN='))
            ?.split('=')[1] ?? '',
    );

const inviteMember = () => {
    if (!props.canManageCollaboration) {
        toast.error('La collaboration est reservee au plan Pro.');

        return;
    }

    inviteError.value = null;

    inviteForm.post(storeProjectMember.url(props.project.id), {
        preserveScroll: true,
        onSuccess: () => {
            toast.success('Membre invite avec succes.');
            inviteForm.reset('email');
            inviteForm.role = 'viewer';
            reloadProject();
        },
        onError: (errors) => {
            inviteError.value = (errors.email as string | undefined) ?? null;
            toast.error("Impossible d'inviter ce membre.");
        },
    });
};

const updateMemberRole = async (member: ProjectMemberType, role: string) => {
    if (!props.canManageCollaboration) {
        toast.error('La collaboration est reservee au plan Pro.');

        return;
    }

    if (member.role === role) {
        return;
    }

    updatingMemberId.value = member.id;

    try {
        const response = await fetch(
            updateProjectMember.url({
                project: props.project.id,
                projectMember: member.id,
            }),
            {
                method: 'PATCH',
                headers: {
                    'Content-Type': 'application/json',
                    Accept: 'application/json',
                    'X-Requested-With': 'XMLHttpRequest',
                    'X-CSRF-TOKEN': csrfToken(),
                    'X-XSRF-TOKEN': xsrfToken(),
                },
                credentials: 'same-origin',
                body: JSON.stringify({ role }),
            },
        );

        if (!response.ok) {
            throw new Error('member-role-update-failed');
        }

        toast.success('Role du membre mis a jour.');
        reloadProject();
    } catch {
        toast.error('Impossible de modifier le role du membre.');
    } finally {
        updatingMemberId.value = null;
    }
};

const removeMember = async (member: ProjectMemberType) => {
    removingMemberId.value = member.id;

    try {
        const response = await fetch(
            destroyProjectMember.url({
                project: props.project.id,
                projectMember: member.id,
            }),
            {
                method: 'DELETE',
                headers: {
                    Accept: 'application/json',
                    'X-Requested-With': 'XMLHttpRequest',
                    'X-CSRF-TOKEN': csrfToken(),
                    'X-XSRF-TOKEN': xsrfToken(),
                },
                credentials: 'same-origin',
            },
        );

        if (!response.ok) {
            throw new Error('member-remove-failed');
        }

        toast.success('Membre retire du projet.');
        reloadProject();
    } catch {
        toast.error('Impossible de retirer ce membre.');
    } finally {
        removingMemberId.value = null;
    }
};

const deleteProject = () => {
    router.delete(destroy.url(props.project.id), {
        onSuccess: () => {
            isDeleteDialogOpen.value = false;
        },
    });
};

const autofillLoading = ref(false);

type AutofillResponse = {
    inclusion_criteria: string[];
    exclusion_criteria: string[];
    languages: string[];
    databases: string[];
    strategy: string;
};

const handleAutofill = async () => {
    if (!form.description?.trim()) {
        toast.error(
            "Ajoute une description de projet avant de lancer l'autofill.",
        );

        return;
    }

    autofillLoading.value = true;

    try {
        const response = await fetch(autofill.url(), {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                Accept: 'application/json',
                'X-Requested-With': 'XMLHttpRequest',
                'X-CSRF-TOKEN':
                    document
                        .querySelector('meta[name="csrf-token"]')
                        ?.getAttribute('content') ?? '',
            },
            credentials: 'same-origin',
            body: JSON.stringify({ description: form.description }),
        });

        if (!response.ok) {
            throw new Error('autofill-failed');
        }

        const data = (await response.json()) as AutofillResponse;

        researchForm.inclusion_criteria = data.inclusion_criteria.join('\n');
        researchForm.exclusion_criteria = data.exclusion_criteria.join('\n');
        researchForm.languages = data.languages.join(', ');
        researchForm.databases = data.databases.join(', ');
        researchForm.search_strategy = data.strategy;

        toast.success('Configuration proposee par IA pre-remplie.');
    } catch {
        toast.error("Impossible de generer l'autofill pour le moment.");
    } finally {
        autofillLoading.value = false;
    }
};
</script>

<template>
    <Head title="Paramètres du Projet" />

    <div class="flex-1 overflow-y-auto">
        <div
            data-tour="settings-overview"
            class="mx-auto max-w-3xl px-10 py-12"
        >
            <!-- Page header -->
            <div class="mb-8 flex items-start justify-between">
                <div>
                    <p
                        class="mb-1 text-xs font-semibold tracking-wider text-muted-foreground uppercase"
                    >
                        Préparation
                    </p>
                    <h1 class="mb-1 text-2xl font-bold tracking-tight">
                        Configuration du projet
                    </h1>
                    <p class="text-sm text-muted-foreground">
                        Définissez la question de recherche, les critères et les
                        sources.
                    </p>
                </div>
                <div class="flex shrink-0 items-center gap-2">
                    <button
                        type="button"
                        :disabled="autofillLoading"
                        class="inline-flex items-center gap-1.5 rounded-md border border-border bg-muted px-3 py-1.5 text-xs font-medium text-foreground hover:bg-muted/80 disabled:opacity-50"
                        @click="handleAutofill"
                    >
                        <Wand2 class="h-3.5 w-3.5" />
                        {{ autofillLoading ? 'Génération...' : 'Pré-remplir' }}
                    </button>
                    <button
                        type="button"
                        :disabled="researchForm.processing"
                        class="inline-flex items-center gap-1.5 rounded-md bg-foreground px-3 py-1.5 text-xs font-medium text-background hover:bg-foreground/90 disabled:opacity-50"
                        @click="submitResearch"
                    >
                        <Save class="h-3.5 w-3.5" />
                        Enregistrer
                    </button>
                </div>
            </div>

            <!-- Bannière onboarding — visible tant que le projet n'est pas configuré -->
            <AiBanner v-if="isNewProject" variant="strong" class="mb-4">
                <template #icon>
                    <span class="text-[11px] font-bold">1/5</span>
                </template>
                <div>
                    <p class="font-medium">
                        Configurez votre protocole de recherche
                    </p>
                    <p class="mt-0.5 text-muted-foreground">
                        Définissez la question de recherche, les critères et les
                        sources. Ces informations guident l'IA pour le screening
                        et les équations.
                    </p>
                    <div
                        class="mt-2 flex flex-wrap items-center gap-1.5 text-[11px]"
                    >
                        <span
                            class="flex items-center gap-1 font-medium text-foreground"
                        >
                            <Check class="h-3 w-3" />
                            Projet créé
                        </span>
                        <span class="text-muted-foreground">→</span>
                        <span class="font-semibold underline underline-offset-2"
                            >Configuration</span
                        >
                        <span class="text-muted-foreground">→</span>
                        <span class="text-muted-foreground">Équations</span>
                        <span class="text-muted-foreground">→</span>
                        <span class="text-muted-foreground">Import</span>
                        <span class="text-muted-foreground">→</span>
                        <span class="text-muted-foreground">Screening</span>
                    </div>
                </div>
            </AiBanner>

            <!-- AI banner -->
            <AiBanner class="mb-6">
                L'IA peut pré-remplir la configuration à partir de la
                description de votre projet. Vérifiez et ajustez les suggestions
                avant d'enregistrer.
            </AiBanner>

            <!-- Question de recherche -->
            <div data-tour="settings-research-question" class="mb-6">
                <div class="mb-3 flex items-center justify-between">
                    <p class="text-sm font-semibold text-foreground">
                        Question de recherche
                    </p>
                </div>
                <Textarea
                    v-model="form.description"
                    :rows="3"
                    class="w-full rounded-lg"
                    placeholder="Décrivez la question ou l'objectif de cette revue systématique..."
                />
            </div>

            <!-- Critères d'inclusion / exclusion -->
            <div
                class="mb-6 grid grid-cols-2 gap-6"
                data-tour="research-config"
            >
                <div>
                    <p class="mb-3 text-sm font-semibold text-foreground">
                        Critères d'inclusion
                    </p>
                    <Textarea
                        v-model="researchForm.inclusion_criteria"
                        :rows="4"
                        class="mb-2 w-full rounded-lg text-sm"
                        placeholder="Un critère par ligne&#10;Ex: Articles publiés après 2015"
                    />
                    <div
                        v-if="researchForm.inclusion_criteria.trim()"
                        class="space-y-1 rounded-lg bg-muted/40 px-3 py-2"
                    >
                        <p
                            class="mb-1.5 text-[10px] font-semibold tracking-wide text-muted-foreground uppercase"
                        >
                            Aperçu
                        </p>
                        <div
                            v-for="(
                                criterion, index
                            ) in researchForm.inclusion_criteria
                                .split('\n')
                                .filter(Boolean)"
                            :key="index"
                            class="flex items-center gap-2 text-sm"
                        >
                            <span class="font-bold text-emerald-600">✓</span>
                            <span>{{ criterion }}</span>
                        </div>
                    </div>
                </div>
                <div>
                    <p class="mb-3 text-sm font-semibold text-foreground">
                        Critères d'exclusion
                    </p>
                    <Textarea
                        v-model="researchForm.exclusion_criteria"
                        :rows="4"
                        class="mb-2 w-full rounded-lg text-sm"
                        placeholder="Un critère par ligne&#10;Ex: Littérature grise"
                    />
                    <div
                        v-if="researchForm.exclusion_criteria.trim()"
                        class="space-y-1 rounded-lg bg-muted/40 px-3 py-2"
                    >
                        <p
                            class="mb-1.5 text-[10px] font-semibold tracking-wide text-muted-foreground uppercase"
                        >
                            Aperçu
                        </p>
                        <div
                            v-for="(
                                criterion, index
                            ) in researchForm.exclusion_criteria
                                .split('\n')
                                .filter(Boolean)"
                            :key="index"
                            class="flex items-center gap-2 text-sm"
                        >
                            <span class="font-bold text-destructive">✗</span>
                            <span>{{ criterion }}</span>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Langues / Période -->
            <div
                data-tour="settings-databases"
                class="mb-6 grid grid-cols-2 gap-6"
            >
                <div>
                    <p class="mb-3 text-sm font-semibold text-foreground">
                        Langues
                    </p>
                    <Input
                        v-model="researchForm.languages"
                        class="w-full rounded-lg"
                        placeholder="Ex: français, anglais"
                    />
                </div>
                <div>
                    <p class="mb-3 text-sm font-semibold text-foreground">
                        Bases de données
                    </p>
                    <Input
                        v-model="researchForm.databases"
                        class="w-full rounded-lg"
                        placeholder="Ex: PubMed, Scopus"
                    />
                </div>
            </div>

            <!-- Stratégie de recherche -->
            <div class="mb-6">
                <p class="mb-3 text-sm font-semibold text-foreground">
                    Stratégie de recherche
                </p>
                <Textarea
                    v-model="researchForm.search_strategy"
                    :rows="4"
                    class="w-full rounded-lg"
                    placeholder="Décrivez votre protocole de recherche..."
                />
            </div>

            <!-- Informations générales -->
            <div class="mb-6">
                <p class="mb-3 text-sm font-semibold text-foreground">
                    Nom du projet
                </p>
                <Input
                    v-model="form.name"
                    class="w-full rounded-lg"
                    placeholder="Ex: Revue de littérature sur l'IA"
                />
                <p
                    v-if="form.errors.name"
                    class="mt-1 text-xs text-destructive"
                >
                    {{ form.errors.name }}
                </p>
            </div>

            <!-- Gestion des membres -->
            <div class="mb-6">
                <p class="mb-3 text-sm font-semibold text-foreground">
                    Membres du projet
                </p>
                <div
                    v-if="!canManageCollaboration"
                    class="mb-3 rounded-lg border border-border bg-muted/50 px-4 py-3 text-xs text-muted-foreground"
                >
                    La collaboration multi-utilisateur est réservée au plan Pro.
                    <Link
                        :href="pricing()"
                        class="ml-1 font-medium underline underline-offset-2"
                    >
                        Voir les tarifs
                    </Link>
                </div>
                <div class="mb-3 flex items-center gap-2">
                    <Input
                        v-model="inviteForm.email"
                        class="rounded-lg"
                        :disabled="!canManageCollaboration"
                        placeholder="email@exemple.com"
                        type="email"
                    />
                    <Select v-model="inviteForm.role">
                        <SelectTrigger
                            class="w-36 rounded-lg"
                            :disabled="!canManageCollaboration"
                        >
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="viewer">Lecteur</SelectItem>
                            <SelectItem value="editor">Éditeur</SelectItem>
                        </SelectContent>
                    </Select>
                    <Button
                        type="button"
                        :disabled="
                            inviteForm.processing || !canManageCollaboration
                        "
                        class="shrink-0 rounded-lg"
                        size="sm"
                        @click="inviteMember"
                    >
                        <UserPlus class="mr-1.5 h-3.5 w-3.5" />
                        Inviter
                    </Button>
                </div>
                <p v-if="inviteError" class="mb-2 text-xs text-destructive">
                    {{ inviteError }}
                </p>
                <div
                    v-if="project.members && project.members.length > 0"
                    class="space-y-2"
                >
                    <div
                        v-for="member in project.members"
                        :key="member.id"
                        class="flex items-center justify-between rounded-lg border border-border/50 px-3 py-2"
                    >
                        <div class="flex items-center gap-3">
                            <div
                                class="flex h-7 w-7 items-center justify-center rounded-full bg-muted text-xs font-bold"
                            >
                                {{
                                    member.user?.name?.charAt(0)?.toUpperCase()
                                }}
                            </div>
                            <div>
                                <p class="text-sm font-medium">
                                    {{ member.user?.name }}
                                </p>
                                <p class="text-xs text-muted-foreground">
                                    {{ member.user?.email }}
                                </p>
                            </div>
                        </div>
                        <div class="flex items-center gap-2">
                            <Select
                                :model-value="member.role"
                                @update:model-value="
                                    (value) =>
                                        typeof value === 'string' &&
                                        updateMemberRole(member, value)
                                "
                            >
                                <SelectTrigger
                                    class="w-28 rounded-lg"
                                    :disabled="
                                        !canManageCollaboration ||
                                        updatingMemberId === member.id
                                    "
                                >
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="viewer"
                                        >Lecteur</SelectItem
                                    >
                                    <SelectItem value="editor"
                                        >Éditeur</SelectItem
                                    >
                                </SelectContent>
                            </Select>
                            <Button
                                type="button"
                                class="text-destructive hover:bg-destructive/10"
                                size="icon"
                                variant="ghost"
                                :disabled="
                                    updatingMemberId === member.id ||
                                    removingMemberId === member.id
                                "
                                @click="removeMember(member)"
                            >
                                <Trash2 class="h-4 w-4" />
                            </Button>
                        </div>
                    </div>
                </div>
                <p v-else class="text-xs text-muted-foreground italic">
                    Aucun membre pour l'instant.
                </p>
            </div>

            <!-- CTA prochaine étape — visible pour les nouveaux projets -->
            <div
                v-if="isNewProject"
                class="mb-6 rounded-lg border border-border bg-muted/30 p-4"
            >
                <p class="mb-1 text-sm font-semibold">
                    Prochaine étape : Équations de recherche
                </p>
                <p class="mb-3 text-xs text-muted-foreground">
                    Enregistrez la configuration ci-dessus, puis générez vos
                    équations de recherche avec l'IA.
                </p>
                <div class="flex flex-wrap gap-2">
                    <Link
                        :href="searchEquationsRoute(project.id).url"
                        class="inline-flex items-center gap-1.5 rounded-md bg-foreground px-3 py-1.5 text-xs font-medium text-background hover:bg-foreground/90"
                    >
                        Générer les équations →
                    </Link>
                    <Link
                        :href="importMethodRoute(project.id).url"
                        class="inline-flex items-center gap-1.5 rounded-md border border-border bg-background px-3 py-1.5 text-xs font-medium hover:bg-muted"
                    >
                        Importer des articles directement
                    </Link>
                </div>
            </div>

            <!-- Form footer -->
            <div
                class="flex items-center justify-between border-t border-border pt-6"
            >
                <button
                    type="button"
                    class="text-xs text-destructive underline-offset-2 hover:underline"
                    @click="isDeleteDialogOpen = true"
                >
                    Supprimer le projet
                </button>
                <div class="flex gap-2">
                    <Link
                        :href="projectShow.url(project.id)"
                        class="inline-flex items-center rounded-md border border-border px-3 py-1.5 text-xs font-medium hover:bg-muted"
                    >
                        Annuler
                    </Link>
                    <button
                        type="button"
                        :disabled="form.processing || researchForm.processing"
                        class="inline-flex items-center gap-1.5 rounded-md bg-foreground px-3 py-1.5 text-xs font-medium text-background hover:bg-foreground/90 disabled:opacity-50"
                        @click="
                            submitBasic();
                            submitResearch();
                        "
                    >
                        <Save class="h-3.5 w-3.5" />
                        Enregistrer
                    </button>
                </div>
            </div>
        </div>
    </div>

    <ConfirmDialog
        :open="isDeleteDialogOpen"
        description="Cette action supprimera toutes les données associées à ce projet. Cette opération est irréversible."
        title="Supprimer définitivement ce projet ?"
        @update:open="isDeleteDialogOpen = $event"
        @confirm="deleteProject"
    />
</template>
